import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { html } from 'common-tags';

import { getProductView_productDetailByMasterId_variants_legalInformation } from '@containers/ProductDetailContainer/queries/__generated__/getProductView';
import { ICharacteristic } from '@containers/ProductListing/model';

import { isEmpty } from '../../../../../helpers/utils';
import messages from '../../../messages';
import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';

import { Characteristic } from './Characteristic';
import {
  SHOW_MORE_INGREDIENTS_TRACKING,
  SHOW_MORE_NOTICES_TRACKING,
  PD_NUTRITION_TABLE_ID,
} from './constants';
import { ExpandableInfo } from './ExpandableInfo';
import { Instructions } from './Instructions';
import {
  Container,
  MarginWrapper,
  Description,
  CharTitle,
  CharactericticsWrapper,
  CharacteristicsTable,
  SectionTitle,
  NutritionTable,
  HairSensitivityTestLink,
} from './styled';
import useIsAboutProductSeoTest from './useIsAboutProductSeoTest';

export interface IAboutProductProps {
  description: string;
  characteristics: ICharacteristic[];
  composition: ICharacteristic[];
  productCode: string;
  isPerfume?: boolean;
  legalInformation?: Omit<
    getProductView_productDetailByMasterId_variants_legalInformation,
    '__typename'
  >;
}

export const AboutProduct: React.FC<IAboutProductProps> = React.memo(
  ({
    description,
    characteristics,
    composition,
    isPerfume,
    productCode,
    legalInformation,
  }) => {
    const { formatMessage } = useIntl();
    const newPdDesignEnabled = useNewPdDesignEnabled();
    const isSeoTest = useIsAboutProductSeoTest(productCode);

    const productChar = isPerfume
      ? composition.concat(characteristics)
      : characteristics;

    const hasDescription = !isEmpty(description);
    const hasChar = productChar.length > 0;

    const htmlDescription = React.useMemo(
      () => html`${description}`,
      [description]
    );

    const hasDetailedInstructions =
      legalInformation &&
      (legalInformation.instructions ||
        legalInformation.document ||
        legalInformation.video);

    if (
      !hasDescription &&
      !hasChar &&
      !hasDetailedInstructions &&
      !legalInformation?.warnings &&
      !legalInformation?.foodComposition &&
      !legalInformation?.nutritionValues
    ) {
      return null;
    }

    const pdCharacteristics = hasChar && (
      <CharactericticsWrapper
        isSeoTest={isSeoTest}
        fullWidth={!hasDescription}
        newPdDesignEnabled={newPdDesignEnabled}
        id="pdCharacteristics"
      >
        {hasDescription && !isSeoTest && (
          <CharTitle newPdDesignEnabled={newPdDesignEnabled}>
            <FormattedMessage
              {...messages[`composition${isPerfume ? 'Perfume' : 'Cosmetics'}`]}
            />
          </CharTitle>
        )}

        <CharacteristicsTable newPdDesignEnabled={newPdDesignEnabled}>
          {productChar.map((item) => (
            <Characteristic
              key={item.name}
              item={item}
              fullWidth={!hasDescription}
            />
          ))}
        </CharacteristicsTable>
      </CharactericticsWrapper>
    );

    return (
      <Container id="pdDescription" newPdDesignEnabled={newPdDesignEnabled}>
        {isSeoTest && pdCharacteristics}

        <MarginWrapper
          fullWidth={!hasChar}
          newPdDesignEnabled={newPdDesignEnabled}
          id={'pd-description-wrapper'}
        >
          {hasDescription && (
            <Description
              newDesign={newPdDesignEnabled}
              id="pd-description-text"
              data-testid="pd-description-text"
              dangerouslySetInnerHTML={{
                __html: htmlDescription,
              }}
            />
          )}

          {hasDetailedInstructions && (
            <>
              <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                <FormattedMessage {...messages.instructions} />
              </SectionTitle>
              <Instructions
                pdfLink={legalInformation.document}
                videoLink={legalInformation.video}
                instructions={legalInformation.instructions}
              />
            </>
          )}

          {legalInformation?.warnings && (
            <>
              <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                <FormattedMessage {...messages.importantNotices} />
              </SectionTitle>
              <ExpandableInfo
                trackingAction={SHOW_MORE_NOTICES_TRACKING}
                info={legalInformation.warnings}
                productCode={productCode}
              />
            </>
          )}

          {legalInformation?.foodComposition && (
            <>
              <SectionTitle newPdDesignEnabled={newPdDesignEnabled}>
                <FormattedMessage {...messages.foodComposition} />
              </SectionTitle>
              <ExpandableInfo
                trackingAction={SHOW_MORE_INGREDIENTS_TRACKING}
                info={legalInformation.foodComposition}
                productCode={productCode}
              />
            </>
          )}

          {legalInformation?.nutritionValues && (
            <NutritionTable
              data-testid={'nutrition-table'}
              id={PD_NUTRITION_TABLE_ID}
              dangerouslySetInnerHTML={{
                __html: legalInformation.nutritionValues,
              }}
            />
          )}

          {legalInformation?.isSensitiveHairProduct && (
            <HairSensitivityTestLink
              href={formatMessage(messages.sensitiveHairWarningUrl)}
              rel={'nofollow'}
              target={'_blank'}
            >
              <FormattedMessage {...messages.sensitiveHairWarningText} />
            </HairSensitivityTestLink>
          )}
        </MarginWrapper>

        {!isSeoTest && pdCharacteristics}
      </Container>
    );
  }
);
